import { SSOSignOutURL, userBaseURL } from "@/constants";
import { useAppDispatch } from "@app/hooks";
import type { SavedUser, SSOLoginRequestPayload } from "@features/auth/types";
import { logout, setCredentials } from "@features/auth/userSlice";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { useEffect } from "react";

interface UserProfileResponse {
  country: string;
}

export const authentication = createApi({
  reducerPath: "authenticationApi",
  baseQuery: fetchBaseQuery({ baseUrl: userBaseURL }),
  refetchOnReconnect: true,
  keepUnusedDataFor: 300, // 5 minutes cache retention
  endpoints: (builder) => ({
    validateLogin: builder.mutation({
      query: (params) => ({
        url: "authentication/login",
        method: "POST",
        body: params,
      }),
    }),
    getUserProfile: builder.mutation<UserProfileResponse, string>({
      query: (email) => ({
        url: `/user/getUserProfile/${email}`,
        method: "GET",
      }),
    }),
  }),
});

export const { useValidateLoginMutation, useGetUserProfileMutation } =
  authentication;

const STORED_USER_VARIABLES: (keyof SavedUser)[] = [
  "accessToken",
  "refreshToken",
  "displayName",
  "email",
  "loginMethod",
  "userID",
  "lastName",
  "firstName",
  "position",
  "employeeId",
  "functUnitName",
  "office",
  "country",
];

// Special handling for token values to avoid double stringification
const TOKEN_KEYS = [
  "accessToken",
  "refreshToken",
  "firstName",
  "lastName",
  "email",
  "country",
];

export const saveLocalUser = (user: Partial<SavedUser> = {}) => {
  STORED_USER_VARIABLES.forEach((key) => {
    const value = user[key];
    if (value !== null && value !== undefined) {
      // Don't stringify token values as they are already strings
      if (TOKEN_KEYS.includes(key)) {
        localStorage.setItem(key, String(value));
      } else {
        localStorage.setItem(key, JSON.stringify(value));
      }
    } else {
      localStorage.removeItem(key);
    }
  });
};

export const useAuthService = () => {
  const dispatch = useAppDispatch();
  const [validateLogin, { data: loginData, isSuccess: isLoginSuccess }] =
    useValidateLoginMutation();

  useEffect(() => {
    if (isLoginSuccess && loginData?.email) {
      // Set credentials immediately without waiting for country fetch
      // Country will be fetched when survey is loaded
      dispatch(setCredentials(loginData));
      saveLocalUser(loginData);
      localStorage.setItem(
        "userId",
        loginData?.userFeatureResponse?.features[0]?.userId ?? "",
      );
      window.location.reload();
    }
  }, [isLoginSuccess, loginData, dispatch]);

  const handleLogin = (params: SSOLoginRequestPayload) => {
    return validateLogin(params);
  };

  const handleSignOut = () => {
    saveLocalUser();
    dispatch(logout());
    if (SSOSignOutURL) {
      try {
        const parsed = new URL(SSOSignOutURL);
        if (parsed.protocol !== "https:") {
          throw new Error("SSO Sign Out URL must use HTTPS");
        }
        window.location.assign(parsed.href);
      } catch {
        throw new Error("Invalid or unsafe SSO Sign Out URL");
      }
    } else {
      throw new Error("No SSO Sign Out Path defined");
    }
  };

  return {
    handleLogin,
    handleSignOut,
  };
};
